export type ContractStatus =
  | "Active"
  | "Decision Pending"
  | "Extension Approved"
  | "New Procurement Initiated"
  | "Lapsed";

export interface Contract {
  id: string;
  supplier: string;
  department: string;
  contractNumber: string;
  category: string;
  value: number;
  startDate: string;
  endDate: string;
  originalTermMonths: number;
  extensionAllowed: boolean;
  extensionConditions: string;
  responsibleOfficer: string;
  status: ContractStatus;
  noticePeriod: number;
}

export type AuditActionType =
  | "created"
  | "alert_sent"
  | "alert_acknowledged"
  | "status_change"
  | "document_uploaded"
  | "note_added"
  | "escalation";

export interface AuditEntry {
  id: string;
  contractId: string;
  action: string;
  actionType: AuditActionType;
  performedBy: string;
  notes: string;
  createdAt: string;
}

export interface AlertConfig {
  id: string;
  tierDays: number;
  escalationDays: number;
  notifyRoles: string[];
}

export const statusColors: Record<ContractStatus, { bg: string; text: string; dot: string }> = {
  "Active": { bg: "bg-secondary-50", text: "text-secondary-700", dot: "bg-secondary-500" },
  "Decision Pending": { bg: "bg-accent-50", text: "text-accent-700", dot: "bg-accent-500" },
  "Extension Approved": { bg: "bg-primary-50", text: "text-primary-700", dot: "bg-primary-500" },
  "New Procurement Initiated": { bg: "bg-background-200", text: "text-foreground-600", dot: "bg-background-400" },
  "Lapsed": { bg: "bg-background-200", text: "text-foreground-600", dot: "bg-background-400" },
};

export const mockContracts: Contract[] = [
  {
    id: "ctr-001",
    supplier: "Dimension Data (Pty) Ltd",
    department: "Department of Home Affairs",
    contractNumber: "DHA/ICT/2024-089",
    category: "IT Infrastructure",
    value: 12450000,
    startDate: "2023-07-01",
    endDate: "2026-06-30",
    originalTermMonths: 36,
    extensionAllowed: true,
    extensionConditions: "Maximum two 12-month extensions subject to performance review",
    responsibleOfficer: "Thabo Molefe",
    status: "Active",
    noticePeriod: 22,
  },
  {
    id: "ctr-002",
    supplier: "Servest Facility Management",
    department: "Department of Public Works",
    contractNumber: "DPW/FM/2023-112",
    category: "Facilities Management",
    value: 8700000,
    startDate: "2023-01-15",
    endDate: "2026-10-15",
    originalTermMonths: 36,
    extensionAllowed: false,
    extensionConditions: "No extension — new procurement must be initiated",
    responsibleOfficer: "Nomsa Dlamini",
    status: "Decision Pending",
    noticePeriod: 15,
  },
  {
    id: "ctr-003",
    supplier: "Deloitte Consulting",
    department: "National Treasury",
    contractNumber: "NT/CON/2023-045",
    category: "Consulting Services",
    value: 22300000,
    startDate: "2022-10-01",
    endDate: "2025-09-30",
    originalTermMonths: 36,
    extensionAllowed: true,
    extensionConditions: "One 12-month extension at same rates, subject to ministerial approval",
    responsibleOfficer: "Sipho Khumalo",
    status: "Extension Approved",
    noticePeriod: 45,
  },
  {
    id: "ctr-004",
    supplier: "Bytes Technology Group",
    department: "SARS",
    contractNumber: "SARS/TECH/2024-203",
    category: "Software Licensing",
    value: 15600000,
    startDate: "2024-03-01",
    endDate: "2025-02-28",
    originalTermMonths: 12,
    extensionAllowed: true,
    extensionConditions: "Annual renewal with 5% price escalation cap",
    responsibleOfficer: "Lindiwe Nkosi",
    status: "New Procurement Initiated",
    noticePeriod: 78,
  },
  {
    id: "ctr-005",
    supplier: "WBHO Construction",
    department: "Department of Transport",
    contractNumber: "DOT/INFRA/2022-076",
    category: "Infrastructure",
    value: 45000000,
    startDate: "2022-06-01",
    endDate: "2025-05-31",
    originalTermMonths: 36,
    extensionAllowed: true,
    extensionConditions: "Extension subject to project completion status and independent audit",
    responsibleOfficer: "Mandla Zulu",
    status: "Active",
    noticePeriod: 92,
  },
  {
    id: "ctr-006",
    supplier: "Gijima Holdings",
    department: "Department of Health",
    contractNumber: "DOH/DIGITAL/2023-167",
    category: "Digital Health Systems",
    value: 9800000,
    startDate: "2023-11-01",
    endDate: "2025-10-31",
    originalTermMonths: 24,
    extensionAllowed: true,
    extensionConditions: "Two 12-month extensions, quarterly performance reviews required",
    responsibleOfficer: "Dr. Palesa Mokoena",
    status: "Active",
    noticePeriod: 62,
  },
  {
    id: "ctr-007",
    supplier: "EOH Mthombo",
    department: "Department of Basic Education",
    contractNumber: "DBE/EDTECH/2022-201",
    category: "Education Technology",
    value: 6700000,
    startDate: "2022-09-01",
    endDate: "2025-08-31",
    originalTermMonths: 36,
    extensionAllowed: false,
    extensionConditions: "Contract ends — new tender to be issued",
    responsibleOfficer: "Busisiwe Mthembu",
    status: "Lapsed",
    noticePeriod: 14,
  },
  {
    id: "ctr-008",
    supplier: "Mediclinic Southern Africa",
    department: "Department of Correctional Services",
    contractNumber: "DCS/HEALTH/2023-093",
    category: "Healthcare Services",
    value: 31200000,
    startDate: "2023-04-01",
    endDate: "2026-03-31",
    originalTermMonths: 36,
    extensionAllowed: true,
    extensionConditions: "Annual renewal with CPI-linked adjustment",
    responsibleOfficer: "Jabulani Sibeko",
    status: "Active",
    noticePeriod: 55,
  },
  {
    id: "ctr-009",
    supplier: "PwC South Africa",
    department: "Auditor-General of South Africa",
    contractNumber: "AGSA/AUDIT/2024-034",
    category: "Audit Services",
    value: 18500000,
    startDate: "2024-01-01",
    endDate: "2025-12-31",
    originalTermMonths: 24,
    extensionAllowed: true,
    extensionConditions: "One 12-month extension subject to AGSA Council approval",
    responsibleOfficer: "Nomvula Dube",
    status: "Decision Pending",
    noticePeriod: 88,
  },
  {
    id: "ctr-010",
    supplier: "BCX",
    department: "Department of Social Development",
    contractNumber: "DSD/CLOUD/2024-118",
    category: "Cloud Services",
    value: 11200000,
    startDate: "2024-06-01",
    endDate: "2025-05-31",
    originalTermMonths: 12,
    extensionAllowed: true,
    extensionConditions: "Monthly rolling after initial term, 90-day notice for changes",
    responsibleOfficer: "Thandiwe Mahlangu",
    status: "Active",
    noticePeriod: 155,
  },
  {
    id: "ctr-011",
    supplier: "SITA (State Information Technology Agency)",
    department: "Department of Defence",
    contractNumber: "DOD/SECURE/2023-052",
    category: "Cybersecurity",
    value: 27800000,
    startDate: "2023-08-01",
    endDate: "2025-07-31",
    originalTermMonths: 24,
    extensionAllowed: true,
    extensionConditions: "Security clearance renewal required for extension",
    responsibleOfficer: "Col. Hendrik van der Merwe",
    status: "Decision Pending",
    noticePeriod: 40,
  },
  {
    id: "ctr-012",
    supplier: "KPMG Services",
    department: "Department of Water and Sanitation",
    contractNumber: "DWS/PMO/2023-144",
    category: "Project Management",
    value: 9400000,
    startDate: "2023-05-01",
    endDate: "2026-04-30",
    originalTermMonths: 36,
    extensionAllowed: false,
    extensionConditions: "Fixed term — no extension clause",
    responsibleOfficer: "Rethabile Mofokeng",
    status: "Active",
    noticePeriod: 28,
  },
];

export const mockAuditLog: AuditEntry[] = [
  // ctr-001 — Dimension Data (Active)
  { id: "aud-001", contractId: "ctr-001", action: "Contract created", actionType: "created", performedBy: "Thabo Molefe", notes: "Initial contract registration in system — IT Infrastructure category, 36-month term", createdAt: "2023-07-01T08:30:00Z" },
  { id: "aud-001b", contractId: "ctr-001", action: "180-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DHA/ICT/2024-089 expires in 180 days. Notified Thabo Molefe (Officer)", createdAt: "2025-12-31T06:00:00Z" },
  { id: "aud-001c", contractId: "ctr-001", action: "Alert Acknowledged by Thabo Molefe", actionType: "alert_acknowledged", performedBy: "Thabo Molefe", notes: "Acknowledged — reviewing extension options with department head", createdAt: "2026-01-03T09:15:00Z" },
  { id: "aud-001d", contractId: "ctr-001", action: "90-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DHA/ICT/2024-089 expires in 90 days. Notified Thabo Molefe (Officer)", createdAt: "2026-03-31T06:00:00Z" },
  { id: "aud-001e", contractId: "ctr-001", action: "Performance review document uploaded", actionType: "document_uploaded", performedBy: "Thabo Molefe", notes: "Uploaded Q4 2025 supplier performance assessment — overall score 4.3/5 (Excellent)", createdAt: "2026-04-10T11:45:00Z" },
  { id: "aud-001f", contractId: "ctr-001", action: "Note added", actionType: "note_added", performedBy: "Thabo Molefe", notes: "Discussed with SCM Manager — preliminary recommendation to extend for 12 months at current rates", createdAt: "2026-04-15T14:20:00Z" },
  { id: "aud-001g", contractId: "ctr-001", action: "60-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DHA/ICT/2024-089 expires in 60 days. Escalation to SCM Manager if not acknowledged within 5 days", createdAt: "2026-04-30T06:00:00Z" },

  // ctr-002 — Servest (Decision Pending)
  { id: "aud-002", contractId: "ctr-002", action: "Contract created", actionType: "created", performedBy: "Nomsa Dlamini", notes: "Initial contract registration — Facilities Management, 36-month term, no extension clause", createdAt: "2023-01-15T09:00:00Z" },
  { id: "aud-002b", contractId: "ctr-002", action: "180-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DPW/FM/2023-112 expires in 180 days", createdAt: "2025-04-18T06:00:00Z" },
  { id: "aud-002c", contractId: "ctr-002", action: "Alert Acknowledged by Nomsa Dlamini", actionType: "alert_acknowledged", performedBy: "Nomsa Dlamini", notes: "Noted — contract has no extension clause, new procurement required", createdAt: "2025-04-25T10:30:00Z" },
  { id: "aud-002d", contractId: "ctr-002", action: "90-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DPW/FM/2023-112 expires in 90 days", createdAt: "2025-07-16T06:00:00Z" },
  { id: "aud-002e", contractId: "ctr-002", action: "Status changed to Decision Pending", actionType: "status_change", performedBy: "Nomsa Dlamini", notes: "Reviewing options — new procurement vs extension not allowed per terms. Engaging procurement team for new tender", createdAt: "2025-11-01T14:15:00Z" },
  { id: "aud-002f", contractId: "ctr-002", action: "Note added", actionType: "note_added", performedBy: "Nomsa Dlamini", notes: "SCM Manager advised to fast-track new tender documentation. Bid specification being drafted.", createdAt: "2025-12-10T08:45:00Z" },

  // ctr-003 — Deloitte (Extension Approved)
  { id: "aud-003", contractId: "ctr-003", action: "Contract created", actionType: "created", performedBy: "Sipho Khumalo", notes: "Initial registration — Consulting Services, 36-month term with extension clause", createdAt: "2022-10-01T08:00:00Z" },
  { id: "aud-003b", contractId: "ctr-003", action: "180-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract NT/CON/2023-045 expires in 180 days", createdAt: "2025-04-02T06:00:00Z" },
  { id: "aud-003c", contractId: "ctr-003", action: "Status changed to Decision Pending", actionType: "status_change", performedBy: "Sipho Khumalo", notes: "Initiated extension review — engaging with ministerial liaison for approval", createdAt: "2025-05-01T10:00:00Z" },
  { id: "aud-003d", contractId: "ctr-003", action: "Note added", actionType: "note_added", performedBy: "Sipho Khumalo", notes: "Supplier submitted performance report — all KPIs met for current term. Strong recommendation for extension.", createdAt: "2025-05-20T13:30:00Z" },
  { id: "aud-003e", contractId: "ctr-003", action: "90-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract NT/CON/2023-045 expires in 90 days", createdAt: "2025-07-02T06:00:00Z" },
  { id: "aud-003f", contractId: "ctr-003", action: "Ministerial approval letter uploaded", actionType: "document_uploaded", performedBy: "Sipho Khumalo", notes: "Ministerial approval received for 12-month extension at same rates", createdAt: "2025-06-10T11:15:00Z" },
  { id: "aud-003g", contractId: "ctr-003", action: "Status changed to Extension Approved", actionType: "status_change", performedBy: "Sipho Khumalo", notes: "Ministerial approval received for 12-month extension. New end date: 30 September 2026.", createdAt: "2025-06-15T09:45:00Z" },
  { id: "aud-003h", contractId: "ctr-003", action: "Alert Acknowledged by Sipho Khumalo", actionType: "alert_acknowledged", performedBy: "Sipho Khumalo", notes: "All extension documentation filed. Contract renewed through September 2026.", createdAt: "2025-06-15T10:00:00Z" },

  // ctr-004 — Bytes Technology (New Procurement Initiated)
  { id: "aud-004", contractId: "ctr-004", action: "Contract created", actionType: "created", performedBy: "Lindiwe Nkosi", notes: "Initial registration — Software Licensing, 12-month term with annual renewal clause", createdAt: "2024-03-01T09:00:00Z" },
  { id: "aud-004b", contractId: "ctr-004", action: "Note added", actionType: "note_added", performedBy: "Lindiwe Nkosi", notes: "Supplier proposed 8% price escalation for renewal — exceeds 5% cap in contract terms. Needs review.", createdAt: "2024-12-15T16:00:00Z" },
  { id: "aud-004c", contractId: "ctr-004", action: "Status changed to Decision Pending", actionType: "status_change", performedBy: "Lindiwe Nkosi", notes: "Price escalation dispute flagged to procurement committee", createdAt: "2025-01-20T11:30:00Z" },
  { id: "aud-004d", contractId: "ctr-004", action: "Status changed to New Procurement Initiated", actionType: "status_change", performedBy: "Lindiwe Nkosi", notes: "RFP issued under tender number SARS/RFP/2025-056 — seeking competitive bids for software licensing", createdAt: "2025-02-10T11:30:00Z" },
  { id: "aud-004e", contractId: "ctr-004", action: "RFP document uploaded", actionType: "document_uploaded", performedBy: "Lindiwe Nkosi", notes: "Uploaded RFP SARS/RFP/2025-056 — bid closing date 15 March 2025", createdAt: "2025-02-10T11:45:00Z" },

  // ctr-005 — WBHO (Active)
  { id: "aud-005", contractId: "ctr-005", action: "Contract created", actionType: "created", performedBy: "Mandla Zulu", notes: "Initial registration — Infrastructure, 36-month term, extension subject to project audit", createdAt: "2022-06-01T08:00:00Z" },
  { id: "aud-005b", contractId: "ctr-005", action: "Independent audit report uploaded", actionType: "document_uploaded", performedBy: "Mandla Zulu", notes: "Project completion audit by PwC — 94% of milestones delivered on schedule", createdAt: "2025-03-15T14:00:00Z" },
  { id: "aud-005c", contractId: "ctr-005", action: "30-day alert escalated to SCM Manager", actionType: "escalation", performedBy: "System", notes: "No acknowledgment from responsible officer within 5 days — escalated to SCM Manager", createdAt: "2025-04-30T06:00:00Z" },
  { id: "aud-005d", contractId: "ctr-005", action: "Alert Acknowledged by Mandla Zulu", actionType: "alert_acknowledged", performedBy: "Mandla Zulu", notes: "Apologies for delay — was awaiting audit sign-off. Extension recommendation: approve 12-month extension.", createdAt: "2025-05-02T09:30:00Z" },
  { id: "aud-005e", contractId: "ctr-005", action: "Status changed to Decision Pending", actionType: "status_change", performedBy: "Mandla Zulu", notes: "Submitted extension request with audit findings to department head", createdAt: "2025-05-05T11:00:00Z" },
  { id: "aud-005f", contractId: "ctr-005", action: "Status changed to Active", actionType: "status_change", performedBy: "Mandla Zulu", notes: "Extension approved by department head — 12-month extension granted, new end date: 31 May 2026", createdAt: "2025-05-20T08:15:00Z" },

  // ctr-006 — Gijima (Active)
  { id: "aud-006", contractId: "ctr-006", action: "Contract created", actionType: "created", performedBy: "Dr. Palesa Mokoena", notes: "Initial registration — Digital Health Systems, 24-month term, quarterly reviews required", createdAt: "2023-11-01T08:30:00Z" },
  { id: "aud-006b", contractId: "ctr-006", action: "60-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DOH/DIGITAL/2023-167 expires in 60 days", createdAt: "2025-08-31T06:00:00Z" },
  { id: "aud-006c", contractId: "ctr-006", action: "Alert Acknowledged by Dr. Palesa Mokoena", actionType: "alert_acknowledged", performedBy: "Dr. Palesa Mokoena", notes: "All quarterly reviews completed with satisfactory ratings. Preparing extension documentation.", createdAt: "2025-09-03T07:45:00Z" },
  { id: "aud-006d", contractId: "ctr-006", action: "Q2 2025 review document uploaded", actionType: "document_uploaded", performedBy: "Dr. Palesa Mokoena", notes: "Quarterly performance review Q2 2025 — Quality 5/5, Timeliness 5/5, Compliance 5/5. Excellent performance.", createdAt: "2025-09-10T10:30:00Z" },

  // ctr-007 — EOH (Lapsed)
  { id: "aud-007", contractId: "ctr-007", action: "Contract created", actionType: "created", performedBy: "Busisiwe Mthembu", notes: "Initial registration — Education Technology, 36-month term, fixed — no extension", createdAt: "2022-09-01T09:00:00Z" },
  { id: "aud-007b", contractId: "ctr-007", action: "180-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DBE/EDTECH/2022-201 expires in 180 days", createdAt: "2025-03-02T06:00:00Z" },
  { id: "aud-007c", contractId: "ctr-007", action: "Note added", actionType: "note_added", performedBy: "Busisiwe Mthembu", notes: "No extension clause — new tender must be issued. Procurement team notified to begin bid specification.", createdAt: "2025-04-01T14:00:00Z" },
  { id: "aud-007d", contractId: "ctr-007", action: "90-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DBE/EDTECH/2022-201 expires in 90 days", createdAt: "2025-05-31T06:00:00Z" },
  { id: "aud-007e", contractId: "ctr-007", action: "30-day alert escalated to SCM Manager", actionType: "escalation", performedBy: "System", notes: "Alert escalated — no acknowledgment received within 5 days", createdAt: "2025-07-31T06:00:00Z" },
  { id: "aud-007f", contractId: "ctr-007", action: "Status changed to Lapsed", actionType: "status_change", performedBy: "Busisiwe Mthembu", notes: "Contract has expired. New tender DBE/EDTECH/2025-312 published on eTenders portal.", createdAt: "2025-09-01T08:00:00Z" },

  // ctr-008 — Mediclinic (Active)
  { id: "aud-008", contractId: "ctr-008", action: "Contract created", actionType: "created", performedBy: "Jabulani Sibeko", notes: "Initial registration — Healthcare Services, 36-month term, CPI-linked annual adjustment", createdAt: "2023-04-01T08:00:00Z" },
  { id: "aud-008b", contractId: "ctr-008", action: "Annual CPI adjustment applied", actionType: "note_added", performedBy: "Jabulani Sibeko", notes: "2024 CPI adjustment of 5.2% applied to contract value. New annual value: R 16,411,200.", createdAt: "2024-04-01T09:00:00Z" },
  { id: "aud-008c", contractId: "ctr-008", action: "Annual CPI adjustment applied", actionType: "note_added", performedBy: "Jabulani Sibeko", notes: "2025 CPI adjustment of 4.8% applied to contract value. New annual value: R 17,198,000.", createdAt: "2025-04-01T09:00:00Z" },
  { id: "aud-008d", contractId: "ctr-008", action: "90-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DCS/HEALTH/2023-093 expires in 90 days", createdAt: "2026-03-31T06:00:00Z" },
  { id: "aud-008e", contractId: "ctr-008", action: "Alert Acknowledged by Jabulani Sibeko", actionType: "alert_acknowledged", performedBy: "Jabulani Sibeko", notes: "Service levels remain excellent across all facilities. Preparing extension recommendation.", createdAt: "2026-04-02T10:15:00Z" },

  // ctr-009 — PwC (Decision Pending)
  { id: "aud-009", contractId: "ctr-009", action: "Contract created", actionType: "created", performedBy: "Nomvula Dube", notes: "Initial registration — Audit Services, 24-month term, AGSA Council approval required for extension", createdAt: "2024-01-01T09:00:00Z" },
  { id: "aud-009b", contractId: "ctr-009", action: "180-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract AGSA/AUDIT/2024-034 expires in 180 days", createdAt: "2025-07-04T06:00:00Z" },
  { id: "aud-009c", contractId: "ctr-009", action: "Status changed to Decision Pending", actionType: "status_change", performedBy: "Nomvula Dube", notes: "Extension request submitted to AGSA Council for September 2025 sitting. Awaiting outcome.", createdAt: "2025-08-01T14:00:00Z" },
  { id: "aud-009d", contractId: "ctr-009", action: "Note added", actionType: "note_added", performedBy: "Nomvula Dube", notes: "AGSA Council sitting postponed to October 2025. Extension decision delayed.", createdAt: "2025-09-15T11:30:00Z" },

  // ctr-010 — BCX (Active)
  { id: "aud-010", contractId: "ctr-010", action: "Contract created", actionType: "created", performedBy: "Thandiwe Mahlangu", notes: "Initial registration — Cloud Services, 12-month term, monthly rolling after initial term", createdAt: "2024-06-01T08:00:00Z" },
  { id: "aud-010b", contractId: "ctr-010", action: "Note added", actionType: "note_added", performedBy: "Thandiwe Mahlangu", notes: "Cloud migration completed ahead of schedule. All workloads migrated to BCX infrastructure. Service levels meeting SLA targets.", createdAt: "2024-09-30T16:00:00Z" },
  { id: "aud-010c", contractId: "ctr-010", action: "Service review document uploaded", actionType: "document_uploaded", performedBy: "Thandiwe Mahlangu", notes: "6-month service review — uptime 99.97%, response times within SLA thresholds. Recommended to continue on monthly rolling basis.", createdAt: "2024-12-15T10:00:00Z" },

  // ctr-011 — SITA (Decision Pending)
  { id: "aud-011", contractId: "ctr-011", action: "Contract created", actionType: "created", performedBy: "Col. Hendrik van der Merwe", notes: "Initial registration — Cybersecurity, 24-month term, security clearance required for extension", createdAt: "2023-08-01T08:30:00Z" },
  { id: "aud-011b", contractId: "ctr-011", action: "60-day alert sent", actionType: "alert_sent", performedBy: "System", notes: "Automated alert: contract DOD/SECURE/2023-052 expires in 60 days", createdAt: "2025-05-31T06:00:00Z" },
  { id: "aud-011c", contractId: "ctr-011", action: "Alert Acknowledged by Col. Hendrik van der Merwe", actionType: "alert_acknowledged", performedBy: "Col. Hendrik van der Merwe", notes: "Alert acknowledged — escalation paused. Security clearance renewal in progress.", createdAt: "2025-06-02T08:20:00Z" },
  { id: "aud-011d", contractId: "ctr-011", action: "Status changed to Decision Pending", actionType: "status_change", performedBy: "Col. Hendrik van der Merwe", notes: "Security clearance renewal submitted to Defence Intelligence. Awaiting outcome before extension can be considered.", createdAt: "2025-06-10T13:00:00Z" },
  { id: "aud-011e", contractId: "ctr-011", action: "Note added", actionType: "note_added", performedBy: "Col. Hendrik van der Merwe", notes: "Defence Intelligence requested additional documentation. Response submitted 18 June 2025.", createdAt: "2025-06-18T09:00:00Z" },

  // ctr-012 — KPMG (Active)
  { id: "aud-012", contractId: "ctr-012", action: "Contract created", actionType: "created", performedBy: "Rethabile Mofokeng", notes: "Initial registration — Project Management, 36-month term, fixed — no extension clause", createdAt: "2023-05-01T08:00:00Z" },
  { id: "aud-012b", contractId: "ctr-012", action: "Note added", actionType: "note_added", performedBy: "Rethabile Mofokeng", notes: "All project milestones on track. KPMG delivering ahead of schedule on water infrastructure programme.", createdAt: "2024-06-15T14:30:00Z" },
  { id: "aud-012c", contractId: "ctr-012", action: "Mid-term review document uploaded", actionType: "document_uploaded", performedBy: "Rethabile Mofokeng", notes: "Independent mid-term review completed — project rated 4.5/5 across all dimensions. Recommend initiating early procurement planning for successor contract.", createdAt: "2024-12-01T11:00:00Z" },
];

export const mockAlertConfigs: AlertConfig[] = [
  { id: "alert-001", tierDays: 180, escalationDays: 14, notifyRoles: ["Officer", "SCM Manager"] },
  { id: "alert-002", tierDays: 90, escalationDays: 7, notifyRoles: ["Officer", "SCM Manager"] },
  { id: "alert-003", tierDays: 60, escalationDays: 5, notifyRoles: ["Officer", "SCM Manager", "CFO/HOD"] },
  { id: "alert-004", tierDays: 30, escalationDays: 3, notifyRoles: ["Officer", "SCM Manager", "CFO/HOD"] },
];

export const mockDepartments = [
  "Department of Home Affairs",
  "Department of Public Works",
  "National Treasury",
  "SARS",
  "Department of Transport",
  "Department of Health",
  "Department of Basic Education",
  "Department of Correctional Services",
  "Auditor-General of South Africa",
  "Department of Social Development",
  "Department of Defence",
  "Department of Water and Sanitation",
];

export const mockCategories = [
  "IT Infrastructure",
  "Facilities Management",
  "Consulting Services",
  "Software Licensing",
  "Infrastructure",
  "Digital Health Systems",
  "Education Technology",
  "Healthcare Services",
  "Audit Services",
  "Cloud Services",
  "Cybersecurity",
  "Project Management",
];